<template>
  <ak-card-header class="flex flex--center">
    <div class="flex__item flex flex--center">
      <h4 class="color-grey">Projects</h4>
    </div>
    <div>
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <ak-button
            size="small"
            circle
            color="transparent"
            @click="changeWidget()"
          >
            <i class="color-grey-light icon-cog" />
          </ak-button>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-y h-full">
    <ak-display-table v-if="isLoading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell>
          <ak-pack>
            <ak-pack-item>
              <div class="c-faux-text width-125" />
              <div>
                <p class="c-faux-text width-100 mb-none"></p>
              </div>
            </ak-pack-item>
          </ak-pack>
        </ak-display-table-cell>
        <ak-display-table-cell class="cell--1">
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>

    <ak-table
      v-model:data="campaigns"
      hoverable
      hide-head
      background
      spacing="medium"
      :empty-state="emptyState"
      :class="{ 'h-full flex': !campaigns.length }"
    >
      <template #default="{ row }">
        <ak-table-column class="cursor-pointer" @click="callModal(row)">
          <div>{{ row.title }}</div>
          <small
            ><em>Last Submission: {{ row.time }}</em></small
          >
        </ak-table-column>
        <ak-table-column
          class="text-nowrap cell--1 cursor-pointer"
          @click="callModal(row)"
        >
          <ak-tag :color="'primary'" badge>{{ row.leadCount }}</ak-tag>
          {{ leadsBranding }}
        </ak-table-column>
      </template>
    </ak-table>
  </ak-card-body>
</template>

<script>
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { defineComponent, ref, computed, onMounted } from 'vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import date from '@akerolabs/akero-vue/src/utils/date'
import { createQueryString } from '@akerolabs/akero-vue/src/utils/helpers.js'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
import { execChangeHandler } from '../../../legacy/router/service'

export default defineComponent({
  name: 'AkWidgetProjects',

  components: {
    [CardHeader.name]: CardHeader,
    [Tag.name]: Tag,
  },

  props: {
    /**
     * @type {Function}
     * @description change widget function
     */
    changeWidget: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {Function}
     * @description call overview modal
     */
    callModal: {
      default: () => ({}),
      type: Function,
    },
  },

  setup() {
    let n = 10
    const campaigns = ref([])
    const isLoading = ref(true)
    const startDate = '2012-01-01T00:00:00.000Z'
    const endDate = date().toISOString()

    /**
     * @description User defined lead branding
     */
    const leadsBranding = computed(() => {
      return entityBranding('leads.plural', 'Leads')
    })

    const q = createQueryString({
      account: accountObj()._id,
      agency: agencyObj()._id,
      deleted: JSON.stringify({ $ne: true }),
      isTest: JSON.stringify({ $ne: true }),
      createdOn: JSON.stringify({ $gte: startDate, $lte: endDate }),
    })

    const leadsAggregate = async () => {
      isLoading.value = true
      let leadsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load leads`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(`/leads/aggregate/${q}`)
        leadsData = data.data
      } catch (ex) {
        leadsData = []
        displayError(ex)
      }
      await fetchCampaigns(leadsData)
    }

    /**
     * @description Makes an api request to request campaigns for account/agency
     */
    const fetchCampaigns = async (result) => {
      let campaignsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/campaigns', campaignsRequest.value)
        campaignsData = data.data
      } catch (ex) {
        campaignsData = []
        displayError(ex)
      }

      for (var j = 0; j < campaignsData.length; j++) {
        for (var i = 0; i < result.length; i++) {
          if (result[i]._id._id === campaignsData[j]._id) {
            let campaign = {}
            campaign._id = result[i]._id._id
            campaign.description = campaignsData[j].description
            campaign.title = result[i]._id.campaignLabel
            campaign.leadCount = result[i].total
            campaign.date = result[i].latestLead
            campaign.time = date(result[i].latestLead).fromNow()
            campaign.latestLead = date(result[i].latestLead).format(
              'dddd, MMMM Do YYYY, h:mm:ss a',
            )
            if (campaign.title) {
              campaigns.value.push(campaign)
              campaigns.value.sort((a, b) => {
                return date(b.date) - date(a.date)
              })
            }
          }
        }
      }

      isLoading.value = false
    }

    /**
     * @description params for fetchLeads()
     */
    const campaignsRequest = computed(() => {
      let params = {
        account: accountObj()._id,
        agency: agencyObj()._id,
        deleted: JSON.stringify({ $ne: true }),
        archived: JSON.stringify({ $ne: true }),
        per_page: 0,
      }
      return {
        params,
      }
    })

    const emptyState = {
      title: "There currently aren't any projects",
      icon: 'briefcase',
      button: 'Add projects',
      click: () =>
        execChangeHandler({
          name: 'advertising.manager.manage.briefs',
          params: { mode: 'brief' },
        }),
    }

    onMounted(async () => {
      await leadsAggregate()
    })

    return {
      campaigns,
      isLoading,
      n,
      leadsBranding,
      emptyState,
    }
  },
})
</script>
